import { useEffect, useState } from "react";
import { X, Search, Loader2 } from "lucide-react";

export function StatusBadge({ status }) {
  const map = {
    green: ["bg-[#DCFCE7] text-[#15803D] border-[#86EFAC]", "Valido"],
    orange: ["bg-[#FFEDD5] text-[#C2410C] border-[#FDBA74]", "In scadenza"],
    red: ["bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]", "Scaduto"],
  };
  const [cls, label] = map[status] || ["bg-slate-100 text-slate-600 border-slate-200", "—"];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />{label}
    </span>
  );
}

export function Pill({ children, className = "" }) {
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${className}`}>{children}</span>;
}

export function Card({ children, className = "" }) {
  return <div className={`bg-white border border-slate-200/80 rounded-xl shadow-sm ${className}`}>{children}</div>;
}

export function Button({ children, variant = "primary", size = "md", className = "", ...props }) {
  const base = "inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-all duration-150 disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2";
  const variants = {
    primary: "bg-slate-900 text-white hover:bg-slate-700",
    accent: "bg-sky-600 text-white hover:bg-sky-700",
    emerald: "bg-emerald-600 text-white hover:bg-emerald-700",
    outline: "border border-slate-300 text-slate-700 bg-white hover:bg-slate-50",
    ghost: "text-slate-600 hover:bg-slate-100",
    danger: "bg-red-600 text-white hover:bg-red-700",
  };
  const sizes = { sm: "text-xs px-3 py-1.5", md: "text-sm px-4 py-2.5", lg: "text-base px-6 py-3" };
  return <button className={`${base} ${variants[variant]} ${sizes[size]} ${className}`} {...props}>{children}</button>;
}

export function Input({ label, className = "", ...props }) {
  return (
    <div className="w-full">
      {label && <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">{label}</label>}
      <input className={`w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent ${className}`} {...props} />
    </div>
  );
}

export function Select({ label, children, className = "", ...props }) {
  return (
    <div className="w-full">
      {label && <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">{label}</label>}
      <select className={`w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500 ${className}`} {...props}>{children}</select>
    </div>
  );
}

export function Textarea({ label, className = "", ...props }) {
  return (
    <div className="w-full">
      {label && <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">{label}</label>}
      <textarea className={`w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500 ${className}`} {...props} />
    </div>
  );
}

export function Modal({ open, onClose, title, children, wide = false }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-8 overflow-y-auto bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? "max-w-3xl" : "max-w-lg"} my-8 animate-fade-up`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold font-display text-slate-800">{title}</h3>
          <button data-testid="modal-close-btn" onClick={onClose} className="text-slate-400 hover:text-slate-700 rounded-lg p-1 hover:bg-slate-100"><X size={20} /></button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-display tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function SearchBar({ value, onChange, placeholder = "Cerca..." }) {
  return (
    <div className="relative">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input data-testid="search-input" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="pl-9 pr-3 py-2 w-full sm:w-64 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
    </div>
  );
}

export function Spinner() {
  return <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="animate-spin" size={28} /></div>;
}

export function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      {Icon && <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4"><Icon size={26} className="text-slate-400" /></div>}
      <p className="text-slate-700 font-semibold">{title}</p>
      {hint && <p className="text-sm text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

export function fmtEur(n) {
  return "€" + (Number(n) || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export function useDebounced(value, delay = 200) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
  return v;
}
